import { gql } from '@apollo/client';
import { useState } from 'react';
import { useCurrentPressroom } from '../../../../../contexts/CurrentPressroomContext';
import {
  useCreateVideoUploadLinkMutation,
  useSyncResourceVideoMutation,
} from '../../../../../generated/hooks';
import type { SyncResourceVideoMutation } from '../../../../../generated/types';

gql`
  mutation CreateVideoUploadLink($pressroomId: ID!) {
    videoCreateScreen9UploadLink(input: { pressroomId: $pressroomId }) {
      error {
        message
      }
      uploadLink {
        id
        screen9Tag
        url
      }
    }
  }

  mutation SyncResourceVideo($input: VideoSyncScreen9UploadInput!) {
    videoSyncScreen9Upload(input: $input) {
      error {
        message
      }
      video {
        id
        header
        license {
          id
        }
        thumbnailUrl
        resourceVideo {
          id
          processed
        }
      }
    }
  }
`;

type Args = {
  onError?: (error?: string) => void;
  onCompleted?: (response: SyncResourceVideoMutation) => void;
};
type VideoUploadErrorResponse = {
  error?: {
    message?: string;
  };
};

type CallFunction = (file: Blob, videoId?: string) => Promise<SyncResourceVideoMutation | null>;

type State = {
  loading: boolean;
  error: boolean;
  data?: SyncResourceVideoMutation | null;
};
const useVideoUpload = ({ onError, onCompleted }: Args = {}): [CallFunction, State] => {
  const { pressroomId } = useCurrentPressroom();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [createLink] = useCreateVideoUploadLinkMutation({
    variables: {
      pressroomId,
    },
  });
  const [syncVideo, { data }] = useSyncResourceVideoMutation({ onCompleted });
  return [
    async (file: Blob, videoId) => {
      setLoading(true);
      setError(false);
      const { data: link } = await createLink();
      if (
        !link?.videoCreateScreen9UploadLink?.uploadLink ||
        link?.videoCreateScreen9UploadLink?.error
      ) {
        setError(true);
        setLoading(false);
        return null;
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tag', link.videoCreateScreen9UploadLink.uploadLink.screen9Tag);
      try {
        const response = await fetch(link.videoCreateScreen9UploadLink.uploadLink.url, {
          method: 'POST',
          body: fd,
        });
        if (!response.ok) {
          const returnValue = (await response.json()) as VideoUploadErrorResponse;
          onError?.(returnValue?.error?.message ?? JSON.stringify(returnValue));
          setError(true);
          setLoading(false);
          return null;
        }

        const { data: syncVideoData } = await syncVideo({
          variables: {
            input: {
              uploadLinkId: link.videoCreateScreen9UploadLink.uploadLink.id,
              pressroomId,
              videoId,
            },
          },
        });
        setLoading(false);

        if (syncVideoData?.videoSyncScreen9Upload?.error) {
          setError(true);
        }
        return syncVideoData || null;
      } catch (_err) {
        onError?.();
        setError(true);
        setLoading(false);
        return null;
      }
    },
    {
      loading,
      error,
      data,
    },
  ];
};

export default useVideoUpload;
